import { DEFAULT_CURRENCY_CODE, ErrorHandler, LOCALE_ID, enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  RouteReuseStrategy,
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { provideServiceWorker } from '@angular/service-worker';
import {
  getApp,
  initializeApp,
  provideFirebaseApp,
} from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  provideAppCheck,
} from '@angular/fire/app-check';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { getRemoteConfig, provideRemoteConfig } from '@angular/fire/remote-config';
import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import localeEnAu from '@angular/common/locales/en-AU';
import { provideAnimations } from '@angular/platform-browser/animations';
import { setupFontAwesome } from './app/font-awesome.config';
import { addIcons } from 'ionicons';
import { documents, documentText, grid, people } from 'ionicons/icons';

if (!environment.useAppCheckDebugToken) {
  Object.defineProperty(window, 'FIREBASE_APPCHECK_DEBUG_TOKEN', {
    get: () => null,
    set: (val) => console.debug('[DEBUG] Prevented setting debug token:', val),
    configurable: true,
  });

  localStorage.removeItem('firebase:debugAppCheckToken');
  console.debug('[DEBUG] Explicitly neutralized AppCheck debug token.');
}

console.warn('[DEBUG] Explicitly set debug token to:', (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN);
console.warn('[DEBUG] LocalStorage token after cleanup:', localStorage.getItem('firebase:debugAppCheckToken'));
// 🔐 Always require the debug-token module – Angular will replace it
require('./firebase/debug-token');

// Log environment and token status
console.warn('App environment at runtime:', environment);
console.warn('[DEBUG] localStorage token:', localStorage.getItem('firebase:debugAppCheckToken'));
console.warn('[DEBUG] window token:', (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN);

// Icons and fonts
addIcons({ 'documents': documents, 'document-text': documentText, 'people': people, 'grid': grid });
setupFontAwesome();

// Production mode
if (environment.production) {
  enableProdMode();
}

// Register Australian locale
registerLocaleData(localeEnAu);

// Bootstrap the app
bootstrapApplication(AppComponent, {
  providers: [
    { provide: LOCALE_ID, useValue: 'en-AU' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'AUD' },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { timeZone: 'Australia/Melbourne' }
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' })
    ),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth(getApp())),
    provideFirestore(() => getFirestore(getApp())),
    provideFunctions(() => getFunctions(getApp(), 'australia-southeast1')),
    provideMessaging(() => getMessaging()),
    providePerformance(() => getPerformance()),
    provideStorage(() => getStorage()),
    provideRemoteConfig(() => getRemoteConfig()),
    provideAppCheck(() => {
      const provider = new ReCaptchaV3Provider(environment.firebase.appCheck.reCaptchaV3SiteKey);
      return initializeAppCheck(getApp(), {
        provider,
        isTokenAutoRefreshEnabled: true,
      });
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !environment.development,
      registrationStrategy: 'registerImmediately',
    }),
    provideAnimations(),
  ]
}).catch((err) => console.error('Bootstrap error', err));